import React, { useState } from "react";
import { Link } from "react-router-dom";
import { datamatesFeatures, integrations } from "./data";
import sec1 from "../../assets/images/svgs/sec1.svg";
import sec2 from "../../assets/images/svgs/sec2.svg";
import sec3 from "../../assets/images/svgs/sec3.svg";

const SubFeatureTab = ({ feature, active, onClick }) => (
  <button
    onClick={onClick}
    className={`
      px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300
      ${active 
        ? 'bg-[#3f8cff] text-white' 
        : 'bg-gray-100 text-[#355177] hover:bg-gray-200'
      }
    `}
  >
    {feature.title}
  </button>
);

const FeatureBenefitSection = ({ feature, reverse = false }) => {
  const [activeSubFeature, setActiveSubFeature] = useState(
    feature.features ? Object.keys(feature.features)[0] : null
  );

  // Determine which demo/image to show
  const getDemoContent = () => {
    if (feature.features && activeSubFeature && feature.features[activeSubFeature]) {
      const subFeature = feature.features[activeSubFeature];
      return {
        demoSrc: subFeature.demoSrc || null,
        imageSrc: subFeature.imageSrc || null
      };
    }
    return {
      demoSrc: feature.demoSrc || null,
      imageSrc: feature.imageSrc || null
    };
  };

  const { demoSrc, imageSrc } = getDemoContent();
  


  // Determine button action
  const getButtonContent = () => {
    if (feature.id === 'current-context') {
      return (
        <a 
          href="https://datamates-docs.myaltimate.com/components/components_overview/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-block"
        >
          <button className="px-6 py-3 bg-[#3f8cff] hover:bg-[#357dd8] text-white font-medium rounded-lg transition-all duration-300 hover:scale-[1.02]">
            See All Features
          </button>
        </a>
      );
    } else if (feature.id === 'analytics-engineering') {
      return (
        <Link to="/analytics">
          <button className="px-6 py-3 bg-[#3f8cff] hover:bg-[#357dd8] text-white font-medium rounded-lg transition-all duration-300 hover:scale-[1.02]">
            Explore
          </button>
        </Link>
      );
    } else if (feature.id === 'platform-engineering') {
      return (
        <Link to="/platform">
          <button className="px-6 py-3 bg-[#3f8cff] hover:bg-[#357dd8] text-white font-medium rounded-lg transition-all duration-300 hover:scale-[1.02]">
            Explore
          </button>
        </Link>
      );
    }
  };

  return (
    <div className="py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          {/* Content Side - Always on the left */}
          <div className="space-y-6">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#3f8cff] mb-4">
                {feature.subtitle}
              </h2>
              {feature.title && (
                <h3 className="text-xl font-bold text-[#082247] mb-4">
                  {feature.title}
                </h3>
              )}
              <p className="text-lg text-[#5e7493] leading-relaxed">
                {feature.description}
              </p>
            </div>
            
            {/* Sub-features tabs for current context section */}
            {feature.features && (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {Object.entries(feature.features).map(([key, subFeature]) => (
                    <SubFeatureTab
                      key={key}
                      feature={subFeature}
                      active={activeSubFeature === key}
                      onClick={() => setActiveSubFeature(key)}
                    />
                  ))}
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-[#5e7493]">
                    <span className="font-semibold text-[#355177]">
                      {feature.features[activeSubFeature]?.title}:
                    </span>{' '}
                    {feature.features[activeSubFeature]?.description}
                  </p>
                </div>
              </div>
            )}
            
            <div className="space-y-3">
              {feature.highlights.map((highlight, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#3f8cff]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-[#3f8cff]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-[#355177]">{highlight}</span>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <div className="pt-4">
              {getButtonContent()}
            </div>
          </div>
          
          {/* Demo/Image Side - Always on the right */}
          <div className="relative">
            <div className="relative rounded-xl overflow-hidden shadow-2xl bg-white">
              {imageSrc ? (
                <div className="p-4">
                  <img 
                    src={imageSrc} 
                    alt={feature.title}
                    className="w-full h-auto rounded-lg"
                  />
                </div>
              ) : demoSrc ? (
                <div style={{position: "relative", width: "100%", height: "400px"}}>
                  <iframe
                    key={`${feature.id}-${activeSubFeature || 'main'}-${demoSrc}`}
                    src={demoSrc}
                    title={`${feature.title} - ${activeSubFeature || 'main'}`}
                    frameBorder="0"
                    allowFullScreen
                    style={{
                      position: "absolute", 
                      top: 0, 
                      left: 0, 
                      width: "100%", 
                      height: "100%",
                      borderRadius: "12px"
                    }}
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
                  <p className="text-gray-500">Demo not available</p>
                </div>
              )}
            </div>
            <div 
              className="absolute -bottom-6 -right-6 w-32 h-32 rounded-full blur-3xl opacity-40"
              style={{ backgroundColor: feature.color }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DatamatesColumns = () => {
  const features = Object.values(datamatesFeatures);

  return (
    <div className="bg-gradient-to-b from-white to-[#f8fafc]">


      {/* Feature Benefit Sections */}
      {features.map((feature, index) => (
        <FeatureBenefitSection 
          key={feature.id} 
          feature={feature} 
        />
      ))}

      {/* Security Section */}
      <div className="text-center px-3 py-14 md:text-left md:pl-[195px] md:pr-[80px] md:py-[122px] bg-white flex flex-col md:flex-row justify-between">
        <div className="flex flex-col items-center md:items-left justify-between mb-12 md:mb-0">
          <div>
            <div className="font-semibold text-[#082247] text-[30px] md:text-[42px] leading-normal">
              Trusted by security teams in leading enterprises
            </div>
            <p className="mt-3 px-3 md:px-0 md:mt-9 font-normal text-[#365178] text-[14px] md:text-[16px]">
              Altimate AI is committed to privacy and security of our customer
              environments. The solution works by using only metadata from
              customer environment, and none of customer data is used for training
              the LLMs.
            </p>
            <div className="mt-6">
              <a 
                href="https://datamates-docs.myaltimate.com/security/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-[#3f8cff] hover:bg-[#357dd8] text-white font-medium rounded-lg transition-all duration-300 hover:scale-[1.02] text-sm"
              >
                Security FAQs
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
          <div className="w-full">
            <img className="hidden md:block mt-16" src={sec2} alt="" />
          </div>
          <img className="md:hidden mt-16" src={sec3} alt="" />
        </div>
        <img src={sec1} alt="" />
      </div>

      {/* Integrations Section */}
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-[#082247] mb-4">
          Instant integration with the whole stack
        </h2>
        <p className="text-lg text-[#5e7493] mb-12 max-w-2xl mx-auto">
          Out-of-the-box connections and flexible APIs make setup a breeze.
        </p>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {integrations.map((integration, index) => (
            integration.isMoreLink ? (
              <a 
                key={index}
                href={integration.href}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105 flex flex-col items-center justify-center group border-2 border-dashed border-[#3f8cff]/30 hover:border-[#3f8cff]/50"
              >
                <div className="mb-3 transition-transform duration-300 group-hover:scale-110">
                  <div className="h-12 w-12 bg-[#3f8cff]/10 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-[#3f8cff]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                </div>
                <span className="text-[#355177] text-sm font-medium text-center">
                  {integration.name}
                  <br />
                  <span className="text-xs text-[#5e7493]">Integrations</span>
                </span>
                <div className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <svg className="w-4 h-4 text-[#3f8cff]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </div>
              </a>
            ) : (
              <div 
                key={index}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105 flex flex-col items-center justify-center group"
              >
                <div className="mb-3 transition-transform duration-300 group-hover:scale-110">
                  {integration.logoPath ? (
                    <img 
                      src={integration.logoPath} 
                      alt={`${integration.name} logo`}
                      className="h-12 w-auto object-contain"
                    />
                  ) : (
                    <div className="h-12 w-12 bg-[#f0f4f8] rounded-lg flex items-center justify-center">
                      <span className="text-[#5e7493] text-sm font-bold">
                        {integration.name.slice(0, 2).toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
                <span className="text-[#355177] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {integration.name}
                </span>
              </div>
            )
          ))}
        </div>
      </div>
    </div>
  );
};

export default DatamatesColumns; 